import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Check } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';

const ViewQuotePage = () => {
  const { token } = useParams();
  const { t, language } = useTranslation();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [signature, setSignature] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [acceptSuccess, setAcceptSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSenderAddress, setShowSenderAddress] = useState(false);
  const [showRecipientAddress, setShowRecipientAddress] = useState(false);
  const signatureCanvasRef = useRef(null);

  const locale = language === 'nl' ? 'nl-NL' : 'en-US';
  const formatCurrency = (value) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Missing token');
      return;
    }
    axios
      .get(`${API_URL}/api/view-quote/${token}`)
      .then((res) => {
        setQuote(res.data);
        setError(null);
      })
      .catch((err) => {
        setError(err.response?.status === 404 ? 'not_found' : err.message);
        setQuote(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const subtotal = quote?.subtotal ?? 0;
  const vat = quote?.vat ?? 0;
  const total = quote?.total ?? 0;
  const items = quote?.items ?? [];

  const getCoords = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const coords = getCoords(e);
    if (!coords) return;
    setIsDrawing(true);
    const ctx = signatureCanvasRef.current.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const coords = getCoords(e);
    if (!coords) return;
    const ctx = signatureCanvasRef.current.getContext('2d');
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const endDraw = (e) => {
    e.preventDefault();
    if (isDrawing && signatureCanvasRef.current) {
      setSignature(signatureCanvasRef.current.toDataURL('image/png'));
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignature('');
    }
  };

  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">{t('viewQuote.loading')}</p>
      </div>
    );
  }

  if (error === 'not_found' || (!quote && token)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-700">{t('viewQuote.quoteNotFound')}</p>
      </div>
    );
  }

  const mainContent = (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-gray-800">{quote?.sender_name || 'VMK Kozijnen B.V.'}</p>
                {showSenderAddress ? (
                  <pre className="text-xs text-gray-600 mt-1 whitespace-pre-wrap font-sans">{quote?.sender_address || ''}</pre>
                ) : (
                  <button type="button" onClick={() => setShowSenderAddress(true)} className="text-xs text-gray-500 mt-0.5 hover:text-gray-700 underline">
                    {t('viewQuote.showMore')}
                  </button>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-800">
                  {quote?.recipient_name || ''}
                </p>
                {showRecipientAddress ? (
                  <pre className="text-xs text-gray-600 mt-1 whitespace-pre-wrap font-sans text-right">{quote?.recipient_address || ''}</pre>
                ) : (
                  <button type="button" onClick={() => setShowRecipientAddress(true)} className="text-xs text-gray-500 mt-0.5 hover:text-gray-700 underline">
                    {t('viewQuote.showMore')}
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-center">
              <span className="inline-block px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded">
                {t('viewQuote.expiresInDays', {
                  days: quote?.expires_in_days ?? 7,
                })}
              </span>
            </div>

            <h1 className="mt-6 text-xl sm:text-2xl font-semibold text-gray-900">
              {t('viewQuote.offerTitle')}
            </h1>
            <p className="mt-2 text-gray-700">
              {t('viewQuote.dearSirMadam')} {quote?.recipient_name || ''},
            </p>

            <div className="mt-8 space-y-6">
              {items.map((item, idx) => (
                <div key={idx} className="border-b border-gray-100 pb-6 last:border-0">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="text-base font-medium text-gray-900">{item.title}</h3>
                    <span className="text-base font-semibold text-gray-900">
                      {formatCurrency(item.price)}
                    </span>
                  </div>
                  {item.included && item.included.length > 0 && (
                    <ul className="mt-3 list-disc list-inside text-sm text-gray-600 space-y-1">
                      {item.included.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('viewQuote.subtotal')}</span>
                <span className="font-medium text-gray-900">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('viewQuote.vat21')}</span>
                <span className="font-medium text-gray-900">{formatCurrency(vat)}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-gray-900">{t('viewQuote.accept')}</h2>
              <p className="mt-2 text-xl font-semibold text-gray-900">
                {t('viewQuote.amount')}: {formatCurrency(total)}
              </p>
              <p className="mt-4 text-sm text-gray-600">
                {t('viewQuote.signatureInstruction')}
              </p>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-700">{t('viewQuote.name')}</Label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 h-9"
                    placeholder={t('viewQuote.name')}
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-700">{t('viewQuote.emailAddress')}</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 h-9"
                    placeholder={t('viewQuote.emailAddress')}
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label className="text-sm text-gray-700">{t('viewQuote.signature')}</Label>
                <canvas
                  ref={signatureCanvasRef}
                  width={400}
                  height={120}
                  className="mt-2 w-full border-2 border-gray-300 rounded bg-white cursor-crosshair touch-none"
                  style={{ maxWidth: '100%', height: '120px' }}
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={endDraw}
                />
                <button
                  type="button"
                  onClick={clearSignature}
                  className="mt-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {t('viewQuote.clearField')}
                </button>
              </div>

              <Button
                className="mt-6 w-full bg-blue-600 hover:bg-blue-700"
                disabled={!name.trim() || !email.trim() || !signature || submitting}
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    await axios.post(
                      `${API_URL}/api/view-quote/${token}/sign`,
                      {
                        recipient_name: name.trim(),
                        recipient_email: email.trim(),
                        signature,
                      }
                    );
                    setAcceptSuccess(true);
                  } catch (err) {
                    alert(t('viewQuote.acceptError') || 'Failed to submit: ' + (err.response?.data?.detail || err.message));
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {submitting ? (t('viewQuote.submitting') || 'Submitting...') : t('viewQuote.acceptOffer')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (acceptSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 sm:p-12 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600 stroke-[2.5]" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('viewQuote.acceptSuccessTitle') || 'Offer accepted'}
          </h2>
          <p className="text-gray-600 mb-8">
            {t('viewQuote.acceptSuccess') || 'Thank you for signing. We have received your acceptance.'}
          </p>
          <Button
            onClick={() => { try { window.close(); } catch (_) {} }}
            className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
          >
            {t('viewQuote.acceptSuccessOk') || 'OK'}
          </Button>
        </div>
      </div>
    );
  }

  return mainContent;
};

export default ViewQuotePage;
